import { KeyActions, useLatest } from '@solinkcorp/solink-components';
import { Button, Input, cn } from '@solinkcorp/solink-ui';
import classNames from 'classnames';
import { type AiNotification, type ScheduledActivityEntity } from 'clients/CallhomeApiTypes';
import { type IEvent } from 'clients/PlatformApi';
import CameraThumbnail from 'components/CameraThumbnail/Aspen/CameraThumbnail';
import { FullEventSpotCheckAnswer } from 'components/EventsPage/Event/SidekickAnswers/SidekickAnswers';
import S3Image from 'components/S3Image/S3Image';
import { type UseEventSpotCheckStructuredOutput } from 'components/SpotCheckPanel/hooks/useEventSpotCheckStructuredOutput';
import { type UseSpotCheckForm } from 'components/SpotCheckPanel/hooks/useSpotCheckForm';
import { type UseSpotCheckPreview } from 'components/SpotCheckPanel/hooks/useSpotCheckPreview';
import { type UseFormattedSpotCheckStructuredOutput } from 'components/SpotCheckPanel/hooks/useSpotCheckStructuredOutput';
import CenteredLoadingSpinner from 'loadingIndicator/CenteredLoadingSpinner';
import { ChevronLeft, ChevronRight as ChevronRightLucide, Pencil } from 'lucide-react';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import LoadingCylonSvg from 'src/images/loading-cylon.svg';
import { LocationStore } from 'stores/LocationStore';
import { useSelector } from 'stores/store';
import isShallowEqual from 'utils/isShallowEqual';
import { getValidationErrorMessage } from 'utils/yupUtils';
import scrollStyles from '../../../Aspen/SpotCheckScroll.module.sass';
import EventDetailsSnapshotOverlay from '../EventDetailsSnapshotOverlay/EventDetailsSnapshotOverlay';
import styles from '../SpotCheckFormView.module.sass';
import SpotCheckForm from './SpotCheckForm';

interface SpotCheckFormViewProps {
  spotCheck?: ScheduledActivityEntity;
  eventSpotCheck?: AiNotification;
  spotCheckForm: UseSpotCheckForm;
  spotCheckPreview: UseSpotCheckPreview;
  createSpotCheckPreview: () => void;
  spotCheckStructuredOutput: UseFormattedSpotCheckStructuredOutput;
  eventSpotCheckStructuredOutput: UseEventSpotCheckStructuredOutput;
  handleGoBack: () => void;
  excludedCameraIds: string[];
  setExcludedCameraIds: React.Dispatch<React.SetStateAction<string[]>>;
  selectedCameraIndex: number;
  setSelectedCameraIndex: React.Dispatch<React.SetStateAction<number>>;
  timezoneId: string;
  formMode: 'create' | 'edit';
  isExistingSpotCheck: boolean;
  enterCameraSelectMode: () => void;
  sentEventSpotCheckRequest: boolean;
  eventSpotCheckAnswer: string;
  eventImagePreviewUrls: string[];
  totalEventsInLast30Days: number | undefined;
  isEventsEstimateBelowLimit: boolean;
  eventsMonthlyQuotaLimit: number | undefined;
  reportEvent: IEvent | undefined;
}
export default function SpotCheckFormView(props: SpotCheckFormViewProps) {
  const {
    spotCheck,
    spotCheckForm,
    spotCheckPreview,
    createSpotCheckPreview,
    spotCheckStructuredOutput,
    eventSpotCheckStructuredOutput,
    handleGoBack,
    excludedCameraIds,
    setExcludedCameraIds,
    selectedCameraIndex,
    setSelectedCameraIndex,
    timezoneId,
    isExistingSpotCheck,
    formMode,
    enterCameraSelectMode,
    sentEventSpotCheckRequest,
    eventSpotCheckAnswer,
    eventImagePreviewUrls,
    totalEventsInLast30Days,
    isEventsEstimateBelowLimit,
    eventsMonthlyQuotaLimit,
    reportEvent,
  } = props;

  const locale = useLocale();

  const [editTitleMode, setEditTitleMode] = React.useState(false);

  const { selectedCameraIds, spotCheckMode } = useSelector(
    (state) => ({
      selectedCameraIds: state.filter.selectedCameraIds ?? [],
      spotCheckMode: state.filter.spotCheckMode,
    }),
    isShallowEqual,
  );

  const hasSelectedCameras = selectedCameraIds.length > 0;

  const latestCreateSpotCheckPreview = useLatest(createSpotCheckPreview);

  React.useEffect(() => {
    if (selectedCameraIds.length && !isExistingSpotCheck) {
      latestCreateSpotCheckPreview.current();
    }
  }, [selectedCameraIds, latestCreateSpotCheckPreview, isExistingSpotCheck]);

  const titleErrorText = getValidationErrorMessage(spotCheckForm.spotCheckValidationError, 'title');

  React.useEffect(() => {
    setEditTitleMode(!!titleErrorText);
  }, [titleErrorText]);

  const spotCheckEvent = spotCheckPreview?.data;

  const selectedCamera = spotCheckEvent?.cameras?.[selectedCameraIndex];

  const isPolling = spotCheckPreview.eventPollingState === 'POLLING';

  const areCameras = !!spotCheckEvent?.cameras.length;

  const isAtleastTwoCameras = (spotCheckEvent?.cameras?.length ?? 0) > 1;

  const previewAnswer = spotCheckEvent?.details?.scheduledActivity?.metadata?.aiChatAnswers;

  const goToNextCamera = () => setSelectedCameraIndex(selectedCameraIndex + 1);

  const goToPreviousCamera = () => setSelectedCameraIndex(selectedCameraIndex - 1);

  const toggleExcludedCamera = () => {
    if (!selectedCamera?.chId) {
      return;
    }
    const cameraId = selectedCamera.chId;

    if (excludedCameraIds.includes(cameraId)) {
      setExcludedCameraIds(excludedCameraIds.filter((id) => id !== cameraId));
    } else {
      setExcludedCameraIds([...excludedCameraIds, cameraId]);
    }
  };

  const isCurrentCameraSelected = excludedCameraIds.includes(selectedCamera?.chId ?? '');

  const previousCameraDisabled = selectedCameraIndex <= 0;
  const nextCameraDisabled = selectedCameraIndex >= (spotCheckEvent?.cameras.length ?? 0) - 1;
  const isLivePreview = spotCheckMode === 'live-preview';
  const isEventSpotCheck = spotCheckMode === 'event';

  const shortcuts = [
    {
      shortcut: 'left',
      action: goToPreviousCamera,
      shouldActionRun: () => !previousCameraDisabled,
    },
    {
      shortcut: 'right',
      action: goToNextCamera,
      shouldActionRun: () => !nextCameraDisabled,
    },
  ];

  const handleBlurTitle = () => {
    if (!titleErrorText) {
      setEditTitleMode(false);
    }
  };
  const [selectedCameraIndexEventSpotCheck, setSelectedCameraIndexEventSpotCheck] =
    React.useState<number>(0);
  const handleNextCameraEventSpotCheck = () => {
    if (
      eventImagePreviewUrls?.length &&
      selectedCameraIndexEventSpotCheck < eventImagePreviewUrls.length - 1
    ) {
      setSelectedCameraIndexEventSpotCheck(selectedCameraIndexEventSpotCheck + 1);
    }
  };
  const handlePreviousCameraEventSpotCheck = () => {
    if (eventImagePreviewUrls?.length && selectedCameraIndexEventSpotCheck > 0) {
      setSelectedCameraIndexEventSpotCheck(selectedCameraIndexEventSpotCheck - 1);
    }
  };
  const shortcutsEventSpotCheck = [
    {
      shortcut: 'left',
      action: handlePreviousCameraEventSpotCheck,
    },
    {
      shortcut: 'right',
      action: handleNextCameraEventSpotCheck,
    },
  ];

  React.useEffect(() => {
    setSelectedCameraIndexEventSpotCheck(0);
  }, [spotCheckForm.selectedEventReport, setSelectedCameraIndexEventSpotCheck]);

  const isAtLeastTwoReportCameras =
    eventImagePreviewUrls?.length && eventImagePreviewUrls.length >= 2;

  const location = reportEvent?.siteId && LocationStore.getLocation(reportEvent?.siteId);

  const isSnapshotAvailable =
    reportEvent &&
    location &&
    isEventSpotCheck &&
    spotCheckForm.selectedEventReport &&
    !!eventImagePreviewUrls?.length &&
    eventImagePreviewUrls[selectedCameraIndexEventSpotCheck];

  const parseEventSpotCheckAnswer = (answer: string) => {
    try {
      return JSON.parse(answer);
    } catch (_error) {
      return answer;
    }
  };

  const isLoadingStructured =
    spotCheckStructuredOutput.isLoading || eventSpotCheckStructuredOutput.isLoading;

  const formHeaderAspen = (
    <div className="tw flex shrink-0 items-center gap-2 bg-background-muted py-3.5 pl-4 pr-8">
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        title={locale.scheduledAIChat.goBack}
        onClick={handleGoBack}
      >
        <ChevronLeft className="size-5" />
      </Button>
      {editTitleMode ? (
        <div className="tw flex min-w-0 flex-1 flex-col gap-1">
          <Input
            value={spotCheckForm.title}
            onChange={(event) => spotCheckForm.setTitle(event.target.value)}
            onBlur={handleBlurTitle}
            autoFocus
            className="tw min-w-0"
            aria-invalid={!!titleErrorText}
          />
          {titleErrorText ? <p className="tw text-status-red caption">{titleErrorText}</p> : null}
        </div>
      ) : (
        <div className="tw flex min-w-0 flex-1 items-center gap-0.5">
          <button
            type="button"
            className="tw min-w-0 truncate text-left text-foreground-primary heading-sm"
            onClick={handleGoBack}
          >
            {spotCheckForm.title}
          </button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="tw shrink-0"
            title="edit"
            onClick={() => setEditTitleMode(true)}
          >
            <Pencil className="size-4" />
          </Button>
        </div>
      )}
    </div>
  );

  const aspenPreviewNavClassName = 'tw flex w-full min-w-0 items-center justify-between gap-3';

  const aspenPreviewNavButtonClassName = 'tw shrink-0 gap-1';
  // Button outline sets dark:bg-transparent, so the pressed fill needs matching dark: classes.
  const aspenPreviewNavOutlineActiveClassName =
    'tw border-interaction-primary-background-default bg-interaction-primary-background-default text-interaction-primary-content-default shadow-none hover:bg-interaction-primary-background-hover hover:text-interaction-primary-content-default dark:border-interaction-primary-background-default dark:bg-interaction-primary-background-default dark:text-interaction-primary-content-default dark:hover:bg-interaction-primary-background-hover dark:hover:text-interaction-primary-content-default';
  const aspenPreviewNavOutlineIdleClassName =
    'tw text-foreground-primary hover:text-foreground-primary';

  const aspenLoadingRow = (testId: string | undefined, label: string) => (
    <div
      className="tw flex items-center justify-center gap-3 rounded-lg border border-border-muted bg-background-surface px-4 py-3 text-foreground-secondary shadow-elevation-1 body-sm"
      data-testid={testId}
    >
      <span>{label}</span>
      <img src={LoadingCylonSvg} alt="" className="tw size-6" />
    </div>
  );

  const previewPane = (
    <div
      className={cn(
        'tw relative z-0 flex h-full min-h-0 min-w-0 flex-col overflow-y-auto bg-background-muted',
        scrollStyles.spotCheckAspenScroll,
      )}
    >
      <div className="tw flex w-full min-w-0 flex-col gap-4 pb-4 pl-4 pr-8 pt-0">
        <div
          className={cn(
            'tw relative flex w-full min-w-0 items-center justify-center overflow-hidden rounded-lg border border-border-muted bg-background-surface shadow-elevation-1',
            'aspect-video max-h-[min(52vh,560px)] min-h-[12rem]',
          )}
        >
          {selectedCamera ? (
            <S3Image
              id={selectedCamera.chId ?? selectedCamera.id}
              url={selectedCamera.thumbnail}
              className={classNames(styles.cameraImage, 'tw max-h-full max-w-full', {
                [styles.excluded]: excludedCameraIds.includes(selectedCamera.chId ?? ''),
              })}
            />
          ) : null}
          {isSnapshotAvailable ? (
            <div className="tw relative flex h-full w-full items-center justify-center bg-background-surface">
              <CameraThumbnail
                cameraId={eventImagePreviewUrls[selectedCameraIndexEventSpotCheck]}
                thumbnailType="snapshot"
                thumbnail={eventImagePreviewUrls[selectedCameraIndexEventSpotCheck]}
                spotCheckImage
                className="tw max-h-full w-full"
              />
              <EventDetailsSnapshotOverlay
                event={reportEvent}
                location={location}
                canExpand={true}
              />
            </div>
          ) : null}
          {isEventSpotCheck && !isSnapshotAvailable && !selectedCamera && !isPolling ? (
            <p className="tw px-6 text-center text-foreground-secondary body-sm">
              {locale.scheduledAIChat.noPreviewAvailable}
            </p>
          ) : null}
          {!isEventSpotCheck && !hasSelectedCameras ? (
            <Button type="button" variant="default" onClick={enterCameraSelectMode}>
              {locale.scheduledAIChat.selectCamerasSpotCheck}
            </Button>
          ) : null}
          {isPolling && !areCameras ? (
            <div
              className="tw flex items-center gap-3 text-foreground-secondary body-sm"
              data-testid="spot-check-loading-preview"
            >
              <span>{locale.scheduledAIChat.loadingPreview}</span>
              <img src={LoadingCylonSvg} alt="" className="tw size-6" />
            </div>
          ) : null}
        </div>

        {isAtleastTwoCameras ? (
          <KeyActions actions={shortcuts}>
            <div className={aspenPreviewNavClassName}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(aspenPreviewNavButtonClassName, aspenPreviewNavOutlineIdleClassName)}
                onClick={goToPreviousCamera}
                disabled={previousCameraDisabled}
              >
                <ChevronLeft className="size-4 shrink-0" />
                {locale.dashboard.previous}
              </Button>
              {!isLivePreview ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={cn(
                    aspenPreviewNavButtonClassName,
                    isCurrentCameraSelected
                      ? aspenPreviewNavOutlineActiveClassName
                      : aspenPreviewNavOutlineIdleClassName,
                  )}
                  aria-pressed={isCurrentCameraSelected}
                  onClick={toggleExcludedCamera}
                  disabled={
                    !isCurrentCameraSelected &&
                    excludedCameraIds.length + 1 >= (spotCheckEvent?.cameras.length ?? 0)
                  }
                >
                  {locale.scheduledAIChat.excludeThisCamera}
                </Button>
              ) : (
                <span className="tw min-w-0 flex-1" />
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(aspenPreviewNavButtonClassName, aspenPreviewNavOutlineIdleClassName)}
                onClick={goToNextCamera}
                disabled={nextCameraDisabled}
              >
                {locale.general.next}
                <ChevronRightLucide className="size-4 shrink-0" />
              </Button>
            </div>
          </KeyActions>
        ) : null}
        {isAtLeastTwoReportCameras ? (
          <KeyActions actions={shortcutsEventSpotCheck}>
            <div className={aspenPreviewNavClassName}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(aspenPreviewNavButtonClassName, aspenPreviewNavOutlineIdleClassName)}
                onClick={handlePreviousCameraEventSpotCheck}
                disabled={selectedCameraIndexEventSpotCheck <= 0}
              >
                <ChevronLeft className="size-4 shrink-0" />
                {locale.dashboard.previous}
              </Button>
              <span className="tw min-w-0 flex-1" />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(aspenPreviewNavButtonClassName, aspenPreviewNavOutlineIdleClassName)}
                onClick={handleNextCameraEventSpotCheck}
                disabled={
                  !eventImagePreviewUrls?.length ||
                  selectedCameraIndexEventSpotCheck >= eventImagePreviewUrls?.length - 1
                }
              >
                {locale.general.next}
                <ChevronRightLucide className="size-4 shrink-0" />
              </Button>
            </div>
          </KeyActions>
        ) : null}

        <div className="tw flex w-full min-w-0 flex-col gap-3">
          {previewAnswer ? (
            <pre
              className={cn(
                styles.spotCheckPreviewAnswer,
                'tw max-h-[40vh] overflow-y-auto rounded-lg border border-border-muted bg-background-surface p-4 text-foreground-primary shadow-elevation-1 body-sm',
                scrollStyles.spotCheckAspenScroll,
              )}
              data-testid={eventSpotCheckAnswer ? undefined : 'spot-check-preview-answer'}
              dangerouslySetInnerHTML={{
                __html: previewAnswer,
              }}
            />
          ) : null}
          {eventSpotCheckAnswer ? (
            <div
              className={cn(
                'tw max-h-[40vh] overflow-y-auto rounded-lg border border-border-muted bg-background-surface p-4 shadow-elevation-1',
                scrollStyles.spotCheckAspenScroll,
              )}
            >
              <FullEventSpotCheckAnswer
                eventSpotCheckAnswer={parseEventSpotCheckAnswer(eventSpotCheckAnswer)}
                classes={cn(styles.spotCheckPreviewAnswer, 'tw text-foreground-primary body-sm')}
                dataTestId="spot-check-preview-answer"
                displayState
                displayReasoning
                displaySynopsis
              />
            </div>
          ) : null}
          {isPolling && !previewAnswer && areCameras
            ? aspenLoadingRow(
                isEventSpotCheck && sentEventSpotCheckRequest
                  ? undefined
                  : 'spot-check-loading-response',
                locale.scheduledAIChat.loadingResponse,
              )
            : null}
          {isEventSpotCheck && sentEventSpotCheckRequest && !eventSpotCheckAnswer
            ? aspenLoadingRow('spot-check-loading-response', locale.scheduledAIChat.loadingResponse)
            : null}
        </div>
      </div>
    </div>
  );

  const formPane = (
    <div className="tw relative z-0 flex h-full min-h-0 min-w-0 flex-col overflow-hidden border-l border-border-muted bg-background-muted">
      <div
        className={cn(
          styles.spotCheckFullHeight,
          'tw min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-4 pl-8 pr-3 pt-4',
          scrollStyles.spotCheckAspenScroll,
        )}
        data-steps-scroll
      >
        <SpotCheckForm
          formMode={formMode}
          spotCheck={spotCheck}
          spotCheckForm={spotCheckForm}
          timezoneId={timezoneId}
          selectedReportId={[spotCheckForm.selectedEventReport?.templateId]}
          totalEventsInLast30Days={totalEventsInLast30Days}
          isEventsEstimateBelowLimit={isEventsEstimateBelowLimit}
          eventsMonthlyQuotaLimit={eventsMonthlyQuotaLimit}
        />
      </div>
    </div>
  );

  return (
    <div className="tw flex h-full min-h-0 flex-col">
      {isLoadingStructured ? (
        <CenteredLoadingSpinner />
      ) : (
        <>
          <div className="tw grid min-h-0 flex-1 grid-cols-[minmax(0,2fr)_minmax(0,1fr)] overflow-hidden bg-background-muted">
            <div className="tw flex min-h-0 min-w-0 flex-col overflow-hidden">
              {formHeaderAspen}
              {previewPane}
            </div>
            {formPane}
          </div>
        </>
      )}
    </div>
  );
}
